/**
 * One tool call, as a single line until you ask for more.
 *
 * Driven by the projection (a `tool_use` block paired with its `tool_result` by
 * toolUseId) instead of the fat agentPanelStore model. Collapsed, the row is the
 * tool name, what it was called ON (the command, the file, the pattern), a
 * status dot, and how much output came back — enough to decide whether to open
 * it. Expanded, it is the full arguments and the full result.
 *
 * A row, not a card: these appear in runs of ten, and ten bordered cards is a
 * wall. The border belongs to the CARD (ToolCallCard), not to each call.
 *
 * A CALL THAT EDITED A FILE IS DIFFERENT (see `editDiff`). Its arguments ARE the
 * change, so it shows a diff instead of them, its collapsed row carries the file
 * and a `+N −N` stat instead of a truncated blob, and a small one opens itself:
 * a two-line change hidden behind a disclosure is a click charged for nothing,
 * which is exactly the reading Cursor's inline diffs get right.
 */

import { ChevronDown, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { StatusDot } from '../../shell/window/StatusDot'
import { EditDiffView, EditStat } from './EditDiffView'
import { describeEdit } from './editDiff'
import { summarizeResult, summarizeToolArgs } from './toolSummary'

/**
 * How many changed lines an edit may have and still unfold itself. Chosen so a
 * typical single-hunk edit is visible where it happened, while a file rewrite
 * stays a row you scroll past — the property ToolCallCard exists to protect.
 */
export const AUTO_OPEN_LINES = 14

export interface ToolResult {
  text: string
  isError?: boolean
}

function stringify(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

export function ToolCallBlock({
  name,
  args,
  result,
  bare = false,
}: {
  name: string
  args: unknown
  result?: ToolResult
  /** Only the panel, always open, with no header row of its own: for a card
   *  whose header already says what the call was (`ToolCallCard`). */
  bare?: boolean
}) {
  // Memoised because it runs an LCS. A thread re-renders on every streamed
  // token, and a run of ten edits would re-diff all ten each time.
  const edit = useMemo(() => describeEdit(name, args), [name, args])
  const [open, setOpen] = useState(
    () =>
      bare || (edit !== null && edit.added + edit.removed <= AUTO_OPEN_LINES),
  )
  const argText = stringify(args)
  const pending = result === undefined
  // An edit says WHICH FILE. `summarizeToolArgs` would say the same thing, but
  // only because `file_path` happens to sit high in its list; for a MultiEdit
  // (whose path is nested) it falls through to the first string it finds, which
  // is a fragment of code.
  const detail = edit?.label || summarizeToolArgs(args)
  const resultHint = summarizeResult(result)

  return (
    <div>
      {bare ? null : (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left hover:bg-muted/50"
        >
          {open ? (
            <ChevronDown size={12} className="shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight
              size={12}
              className="shrink-0 text-muted-foreground"
            />
          )}
          <StatusDot
            status={pending ? 'working' : result.isError ? 'error' : 'ok'}
            title={pending ? 'running' : undefined}
          />
          <span className="shrink-0 font-mono text-[0.75rem]">{name}</span>
          {detail ? (
            // truncate, not wrap: the row must stay one line tall in a run of ten,
            // and the untruncated text is one click away.
            <span
              className="min-w-0 flex-1 truncate font-mono text-[0.7rem] text-muted-foreground"
              title={detail}
            >
              {detail}
            </span>
          ) : (
            <span className="flex-1" />
          )}
          {/* An edit's result is a sentence saying it worked, so "1 line" tells
            nobody anything. What the row owes you is the size of the change. A
            failure still reports as a failure: the edit did not happen. */}
          {edit && !result?.isError ? (
            <EditStat preview={edit} />
          ) : resultHint ? (
            <span
              className={`shrink-0 text-[0.65rem] ${
                result?.isError ? 'text-red-400' : 'text-muted-foreground/70'
              }`}
            >
              {resultHint}
            </span>
          ) : null}
        </button>
      )}

      {open && (
        <div
          className={
            bare ? 'my-1 px-1' : 'mb-1 ml-5 border-border border-l pl-2'
          }
        >
          {/* THE DIFF REPLACES THE ARGUMENTS, it does not accompany them. The
              two say the same thing, and printing both would put the wall of
              JSON back on the screen directly under its readable form. */}
          {edit ? (
            <EditDiffView preview={edit} />
          ) : (
            argText && (
              <>
                <div className="mb-1 text-[10px] text-muted-foreground uppercase tracking-wide">
                  Arguments
                </div>
                <pre className="mb-2 overflow-x-auto rounded bg-black/40 p-2 font-mono text-[0.75rem] leading-relaxed">
                  {argText}
                </pre>
              </>
            )
          )}
          {/* An edit's success message is one sentence of boilerplate the diff
              already showed. An ERROR is the opposite: it is the only place the
              reason the edit did not happen exists. */}
          {result && !(edit && !result.isError) && (
            <>
              <div className="mb-1 text-[10px] text-muted-foreground uppercase tracking-wide">
                {result.isError ? 'Error' : 'Result'}
              </div>
              <pre
                className={`overflow-x-auto rounded bg-black/40 p-2 font-mono text-[0.75rem] leading-relaxed ${
                  result.isError ? 'text-red-300' : ''
                }`}
              >
                {result.text || '(no output)'}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  )
}
